/**
 * Local row shape → request body shape.
 *
 * The app's domain objects are camelCase; the API is snake_case (see
 * `ServerCaptureRow` and friends in `@kapture/types`, which is how the *pull*
 * side already reads them). The push side was sending the domain object
 * straight out, and the two failure modes were different but both bad:
 *
 * - `capture.upsert` 422'd on the missing `session_id`. A 422 is not
 *   retryable, so every photo, voice note, and GPS fix the crew recorded
 *   became a permanently dead task in the queue.
 * - `session.upsert` returned 200 and silently dropped `templateId`, because
 *   the server's field is optional. The session synced with no template — so
 *   it had no form for a reviewer to read, and extraction skipped its
 *   captures for having no fields to extract into.
 *
 * Mapping at dispatch rather than at enqueue keeps it in one place and means
 * tasks already queued by an older build still go out correctly.
 */
import type { SyncOp } from '@kapture/types';

/** Drop keys the server doesn't accept, so an unknown extra can't 422 us. */
function pick<T extends Record<string, unknown>>(
  source: T,
  keys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
}

function asRecord(payload: unknown): Record<string, unknown> {
  return (payload ?? {}) as Record<string, unknown>;
}

/**
 * What the server stores as columns. Everything else on a capture is
 * kind-specific and travels in `payload`.
 */
const CAPTURE_COLUMNS = new Set([
  'id',
  'sessionId',
  'kind',
  'gps',
  'tags',
  'aiFields',
  // Listed here so they stay out of the kind-specific `payload` spill-over,
  // and absent from the body below for the same reason `aiFields` is: the
  // extraction worker owns them. A client that could set these could tell the
  // office its photographs had been analysed.
  'aiStatus',
  'aiDetail',
  'linkedFieldId',
  'subjectRef',
  'byteSize',
  'capturedAt',
  'createdAt',
  'updatedAt',
  // Device-local: the server identifies the binary by `media_id`, which it
  // assigns itself on upload.
  'blobPath',
]);

/**
 * `ai_fields` is deliberately absent: it is written by the extraction worker,
 * and the server treats an omitted value as "leave it alone". Sending the
 * local copy back would race the worker and erase suggestions.
 *
 * `payload` carries the kind-specific half of the record — a voice note's
 * `durationMs` and `transcript`, a note's `title` and `body`, a barcode's
 * `value` and `format`, a photo's `annotations`. It used to read
 * `row.payload ?? {}`, and a `Capture` has no `payload` field, so every upsert
 * sent `{}`: none of that ever reached the server, and a capture pulled back
 * to another device could not be reconstructed at all.
 */
function captureBody(payload: unknown): Record<string, unknown> {
  const row = asRecord(payload);
  const kindSpecific: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!CAPTURE_COLUMNS.has(key)) kindSpecific[key] = value;
  }
  return {
    session_id: row.sessionId,
    kind: row.kind,
    gps: row.gps ?? null,
    tags: row.tags ?? [],
    linked_field_id: row.linkedFieldId ?? null,
    subject_ref: row.subjectRef ?? null,
    // The one timestamp only the device can supply. `created_at` is not sent
    // and should not be: the server's own arrival stamp is the honest upper
    // bound, and letting a client set it would hand the field the ability to
    // date its own record. Null on a row that predates the column, and the
    // server reads null as "leave whatever you have".
    captured_at: row.capturedAt ?? null,
    payload: kindSpecific,
  };
}

function sessionBody(payload: unknown): Record<string, unknown> {
  const row = asRecord(payload);
  return {
    title: row.title,
    template_id: row.templateId ?? null,
    project_id: row.projectId ?? null,
    status: row.status ?? null,
    reviewer_id: row.reviewerId ?? null,
    meta: row.meta ?? {},
    start_location: row.startLocation ?? null,
    review_note: row.reviewNote ?? null,
    flagged_fields: row.flaggedFields ?? [],
  };
}

function fieldValueBody(payload: unknown): Record<string, unknown> {
  const row = asRecord(payload);
  return {
    value: row.value ?? null,
    source: row.source ?? 'manual',
    confidence: row.confidence ?? null,
    source_capture_id: row.sourceCaptureId ?? null,
  };
}

/** The submit endpoint takes only `base_version`, which the caller sends as
 * `If-Match`. Everything else the phone tracks about a submission is local. */
function submitBody(): Record<string, unknown> {
  return {};
}

export function toWire(op: SyncOp, payload: unknown): unknown {
  switch (op) {
    case 'capture.upsert':
      return captureBody(payload);
    case 'session.upsert':
      return sessionBody(payload);
    case 'session.submit':
      return submitBody();
    case 'fieldValue.upsert':
      return fieldValueBody(payload);
    default:
      // media/signature uploads are multipart and carry no JSON body;
      // capture.delete has none either.
      return pick(asRecord(payload), []);
  }
}
